import React, { useState, useMemo } from 'react'
import { useNavigate, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Loader2, Moon, Sun, Pill, Sunrise, Check, Bed, RotateCcw, Flame, Sparkles } from 'lucide-react'
import { ResponsiveContainer, ComposedChart, Line, ReferenceLine, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import { Card, CardContent } from '../../components/ui/card'
import { useSleep } from '../../hooks/useSleep'
import { useAuth } from '../../contexts/AuthContext'
import { isOwner } from '../../lib/owner'
import { DEFAULTS, labelTime, nightMin, fmtTime } from '../../components/sleep/logic'
import SleepStoryCard from './components/SleepStoryCard'
import { cn } from '../../lib/utils'

export function SleepPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { plan, currentStep, todayLog, recentLogs, daysToTarget, loading, createPlan, logToday, endPlan } = useSleep()

  if (user && !isOwner(user)) {
    return <Navigate to="/" replace />
  }

  return (
    <div className="space-y-4 content-fade pb-8">
      <div className="flex items-center gap-2 mb-2">
        <button type="button" onClick={() => navigate('/')} aria-label={t('settings.back')} className="p-2 -ms-2 rounded-md hover:bg-accent text-foreground">
          <ArrowLeft className="w-5 h-5 rtl:-scale-x-100" />
        </button>
        <h1 className="flex-1 min-w-0 text-lg font-bold text-foreground truncate" dir="auto">{t('sleep.title')}</h1>
        {plan && (
          <button type="button" onClick={endPlan} aria-label={t('sleep.endPlan')} className="p-2 rounded-md hover:bg-accent text-muted-foreground">
            <RotateCcw className="w-5 h-5" />
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-muted-foreground animate-spin" /></div>
      ) : plan ? (
        <DailyView plan={plan} currentStep={currentStep} todayLog={todayLog} recentLogs={recentLogs} daysToTarget={daysToTarget} logToday={logToday} t={t} />
      ) : (
        <SetupForm onCreate={createPlan} t={t} />
      )}
    </div>
  )
}

function DailyView({ plan, currentStep, todayLog, recentLogs, daysToTarget, logToday, t }) {
  const bed = currentStep?.bed_target || plan.target_bedtime
  const wake = currentStep?.wake_target || plan.target_waketime
  const mel = currentStep?.melatonin_time || plan.current_melatonin_time
  const [bedInput, setBedInput] = useState(todayLog?.actual_bedtime?.slice(0, 5) || '')
  const [wakeInput, setWakeInput] = useState(todayLog?.actual_waketime?.slice(0, 5) || '')
  const isMaintenance = plan.status === 'maintenance'

  // Adherence streak: consecutive recent days (today backward) scoring
  // 70+. recentLogs is day-descending.
  const streak = useMemo(() => {
    let n = 0
    for (const l of (recentLogs || [])) {
      if ((l.adherence_score ?? 0) >= 70) n += 1
      else break
    }
    return n
  }, [recentLogs])

  const Lever = ({ icon: Icon, label, done, onToggle }) => (
    <button type="button" onClick={onToggle}
      className={cn('w-full p-3 flex items-center gap-3 rounded-xl', done ? 'bg-app-primary-500/15' : 'bg-muted')} dir="auto">
      <span className={cn('w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0', done ? 'bg-app-primary-500 text-white' : 'bg-background text-muted-foreground')}>
        {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
      </span>
      <span className={cn('text-sm flex-1 text-start', done ? 'text-foreground font-medium' : 'text-foreground')}>{label}</span>
    </button>
  )

  return (
    <div className="space-y-4">
      {/* Maintenance banner — plan has reached target; suggest tapering. */}
      {isMaintenance && (
        <div className="p-3 rounded-xl bg-emerald-500/10 flex items-start gap-3" dir="auto">
          <span className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-4 h-4" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">{t('sleep.maintenanceTitle')}</p>
            <p className="text-[12px] text-muted-foreground mt-0.5">{t('sleep.taperHint')}</p>
          </div>
        </div>
      )}

      {/* Streak */}
      {streak > 0 && (
        <div className="flex items-center justify-center gap-2 text-amber-500" dir="auto">
          <Flame className="w-4 h-4" />
          <span className="text-sm font-semibold">{t('sleep.streakN', { n: streak })}</span>
        </div>
      )}

      {/* Today's targets */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground" dir="auto">{t('sleep.todayTargets')}</span>
            <span className="text-[11px] text-app-primary-500 font-semibold" dir="auto">
              {isMaintenance ? t('sleep.maintenance') : `${t('sleep.stepN', { n: currentStep?.step_no || 1 })}${daysToTarget != null ? ` · ${t('sleep.daysToTarget', { n: daysToTarget })}` : ''}`}
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Target icon={Pill} label={t('sleep.melatonin')} value={labelTime(mel)} />
            <Target icon={Moon} label={t('sleep.bedtime')} value={labelTime(bed)} />
            <Target icon={Sunrise} label={t('sleep.wake')} value={labelTime(wake)} />
          </div>
          {plan.melatonin_dose && (
            <p className="text-[11px] text-muted-foreground text-center mt-2" dir="auto">{t('sleep.doseHint', { dose: plan.melatonin_dose })}</p>
          )}
        </CardContent>
      </Card>

      {/* Checklist */}
      <div className="space-y-2">
        <Lever icon={Sun} label={t('sleep.morningLight')} done={!!todayLog?.morning_light} onToggle={() => logToday({ morning_light: !todayLog?.morning_light })} />
        <Lever icon={Pill} label={t('sleep.melatoninTaken')} done={!!todayLog?.melatonin_taken} onToggle={() => logToday({ melatonin_taken: !todayLog?.melatonin_taken, melatonin_time: mel })} />
        <Lever icon={Bed} label={t('sleep.windDownDone')} done={!!todayLog?.wind_down} onToggle={() => logToday({ wind_down: !todayLog?.wind_down })} />
      </div>

      {/* AI wind-down audio — generated piece spoken via Azure TTS */}
      <SleepStoryCard />

      {/* Log actual times */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground" dir="auto">{t('sleep.logNight')}</span>
          <div className="grid grid-cols-2 gap-3">
            <TimeField label={t('sleep.actualBedtime')} value={bedInput} onChange={setBedInput} onBlur={() => bedInput && logToday({ actual_bedtime: bedInput })} />
            <TimeField label={t('sleep.actualWake')} value={wakeInput} onChange={setWakeInput} onBlur={() => wakeInput && logToday({ actual_waketime: wakeInput })} />
          </div>
          <div>
            <p className="text-[11px] text-muted-foreground mb-1" dir="auto">{t('sleep.quality')}</p>
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].map((q) => (
                <button key={q} type="button" onClick={() => logToday({ sleep_quality: q })}
                  className={cn('flex-1 h-9 rounded-lg text-sm font-semibold', todayLog?.sleep_quality === q ? 'bg-app-primary-500 text-white' : 'bg-muted text-muted-foreground')}>
                  {q}
                </button>
              ))}
            </div>
          </div>
          {todayLog?.adherence_score != null && (
            <p className="text-[12px] text-center" dir="auto">
              <span className="text-muted-foreground">{t('sleep.todayAdherence')} </span>
              <span className={cn('font-bold', todayLog.adherence_score >= 70 ? 'text-emerald-500' : todayLog.adherence_score >= 40 ? 'text-amber-500' : 'text-red-500')}>{todayLog.adherence_score}%</span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Convergence chart — actual bedtime trending toward target. */}
      <ConvergenceChart recentLogs={recentLogs} targetBed={plan.target_bedtime} t={t} />
    </div>
  )
}

// Plots logged actual bedtime over time against the target bedtime
// reference line, so the user sees their sleep onset converging toward
// goal. Times are charted on the "night scale" (early-morning rolls past
// midnight) and rendered back to HH:MM on the axis + tooltip.
function ConvergenceChart({ recentLogs, targetBed, t }) {
  const data = useMemo(() => {
    const withBed = (recentLogs || []).filter((l) => l.actual_bedtime)
    return withBed
      .slice()
      .reverse() // ascending by day
      .map((l) => ({
        date: new Date(`${l.day}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        bed: nightMin(l.actual_bedtime),
      }))
  }, [recentLogs])

  if (data.length < 2) return null
  const targetMin = nightMin(targetBed)
  const vals = data.map((d) => d.bed).concat([targetMin])
  const lo = Math.min(...vals) - 20
  const hi = Math.max(...vals) + 20

  const Tip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null
    return (
      <div className="bg-gray-900 text-white px-3 py-2 rounded-lg text-sm" dir="ltr">
        <p className="text-muted-foreground text-xs">{label}</p>
        <p className="font-mono font-bold text-app-primary-400">{fmtTime(payload[0].value)}</p>
      </div>
    )
  }

  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2" dir="auto">{t('sleep.chartTitle')}</p>
        <div dir="ltr" className="w-full">
          <ResponsiveContainer width="100%" height={200}>
            <ComposedChart data={data} margin={{ top: 5, right: 8, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: '"JetBrains Mono", monospace' }} stroke="#6B7280" />
              <YAxis domain={[lo, hi]} tickFormatter={fmtTime} tick={{ fontSize: 10, fontFamily: '"JetBrains Mono", monospace' }} stroke="#6B7280" width={48} />
              <Tooltip content={<Tip />} />
              <ReferenceLine y={targetMin} stroke="#10b981" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="bed" stroke="hsl(var(--app-primary-500))" strokeWidth={2}
                dot={{ r: 3, fill: 'hsl(var(--app-primary-500))', strokeWidth: 0 }} activeDot={{ r: 5 }} isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-2 flex items-center gap-2 text-[11px] text-muted-foreground" dir="auto">
          <span className="inline-block w-3 h-0.5 bg-emerald-500" aria-hidden="true" />
          {t('sleep.chartTarget', { time: labelTime(targetBed) })}
        </div>
      </CardContent>
    </Card>
  )
}

function Target({ icon: Icon, label, value }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <Icon className="w-4 h-4 text-app-primary-500" />
      <span className="text-base font-bold text-foreground tabular-nums" dir="ltr">{value}</span>
      <span className="text-[10px] text-muted-foreground" dir="auto">{label}</span>
    </div>
  )
}

function TimeField({ label, value, onChange, onBlur }) {
  return (
    <label className="block">
      <span className="text-[11px] text-muted-foreground" dir="auto">{label}</span>
      <input type="time" value={value} onChange={(e) => onChange(e.target.value)} onBlur={onBlur} dir="ltr"
        className="w-full h-10 px-2 mt-1 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none" />
    </label>
  )
}

function SetupForm({ onCreate, t }) {
  const [f, setF] = useState({
    start_bedtime: '02:00', start_waketime: '10:00',
    target_bedtime: DEFAULTS.target_bedtime, target_waketime: DEFAULTS.target_waketime,
    shift_increment: DEFAULTS.shift_increment, hold_days_per_step: DEFAULTS.hold_days_per_step,
    melatonin_hours: DEFAULTS.melatonin_offset / 60, melatonin_dose: DEFAULTS.melatonin_dose,
  })
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }))
  const valid = f.start_bedtime && f.start_waketime && f.target_bedtime && f.target_waketime

  const submit = async () => {
    if (!valid || busy) return
    setBusy(true)
    await onCreate({
      start_bedtime: f.start_bedtime, start_waketime: f.start_waketime,
      target_bedtime: f.target_bedtime, target_waketime: f.target_waketime,
      shift_increment: Number(f.shift_increment) || 30,
      hold_days_per_step: Number(f.hold_days_per_step) || 2,
      melatonin_offset: Math.round((Number(f.melatonin_hours) || 5) * 60),
      melatonin_dose: f.melatonin_dose,
    })
    setBusy(false)
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground px-1" dir="auto">{t('sleep.setupIntro')}</p>
      <Card>
        <CardContent className="p-4 space-y-3">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground" dir="auto">{t('sleep.current')}</span>
          <div className="grid grid-cols-2 gap-3">
            <TimeField label={t('sleep.bedtime')} value={f.start_bedtime} onChange={(v) => set('start_bedtime', v)} />
            <TimeField label={t('sleep.wake')} value={f.start_waketime} onChange={(v) => set('start_waketime', v)} />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4 space-y-3">
          <span className="text-[11px] uppercase tracking-wider text-muted-foreground" dir="auto">{t('sleep.target')}</span>
          <div className="grid grid-cols-2 gap-3">
            <TimeField label={t('sleep.bedtime')} value={f.target_bedtime} onChange={(v) => set('target_bedtime', v)} />
            <TimeField label={t('sleep.wake')} value={f.target_waketime} onChange={(v) => set('target_waketime', v)} />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4 grid grid-cols-2 gap-3">
          <NumField label={t('sleep.shiftIncrement')} value={f.shift_increment} onChange={(v) => set('shift_increment', v)} suffix={t('sleep.minutes')} />
          <NumField label={t('sleep.holdDays')} value={f.hold_days_per_step} onChange={(v) => set('hold_days_per_step', v)} suffix={t('sleep.days')} />
          <NumField label={t('sleep.melatoninOffset')} value={f.melatonin_hours} onChange={(v) => set('melatonin_hours', v)} suffix={t('sleep.hours')} />
          <label className="block">
            <span className="text-[11px] text-muted-foreground" dir="auto">{t('sleep.dose')}</span>
            <input type="text" value={f.melatonin_dose} onChange={(e) => set('melatonin_dose', e.target.value)} dir="auto"
              className="w-full h-10 px-2 mt-1 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none" />
          </label>
        </CardContent>
      </Card>
      <button type="button" onClick={submit} disabled={!valid || busy}
        className="w-full py-3.5 rounded-xl bg-app-primary-500 text-white font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2" dir="auto">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Moon className="w-5 h-5" />} {t('sleep.startPlan')}
      </button>
      <p className="text-[11px] text-muted-foreground text-center px-4" dir="auto">{t('sleep.disclaimer')}</p>
    </div>
  )
}

function NumField({ label, value, onChange, suffix }) {
  return (
    <label className="block">
      <span className="text-[11px] text-muted-foreground" dir="auto">{label}</span>
      <div className="flex items-center gap-1.5 mt-1">
        <input type="number" inputMode="numeric" value={value} onChange={(e) => onChange(e.target.value)} dir="ltr"
          className="w-full h-10 px-2 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none" />
        <span className="text-[11px] text-muted-foreground flex-shrink-0" dir="auto">{suffix}</span>
      </div>
    </label>
  )
}

export default SleepPage
